/**
 * Phase 7C-4 -- the Website's client for the Phase 7C-2 auth contract,
 * `POST /api/v1/auth/session`, selected by the `operation` header exactly
 * like every other jinda-pos-api route.
 *
 *   SESSION_CREATE   public; verifies credentials and returns the token
 *                    (the ONLY response that ever contains it)
 *   SESSION_ME       bearer; validates the token and returns its owner
 *   SESSION_REVOKE   bearer; signs this one session out
 *
 * `SESSION_REVOKE_ALL` exists on the Backend but has no Website UI in this
 * phase, so it is deliberately not wrapped here.
 *
 * The legacy `POST /api/v1/other/employee` LOGIN/LOGOUT is NOT called for
 * authentication any more. It returns no token, answers a failed login
 * with HTTP 200 `{}`, and its 15-minute `session_login` lock is not what
 * decides whether the Website may sign in.
 *
 * Field names below are copied from `handler/auth/session/sessionService.py`
 * and `common/auth.py::actor_public_view`, not from memory.
 */
import axios from 'axios';
import { base_url } from '../api/api-config';
import { AuthApiError, MALFORMED_RESPONSE_MESSAGE, toAuthApiError } from './auth-errors';

export const SESSION_ENDPOINT = `${base_url}/api/v1/auth/session`;

/** One of Backend `common/session.py::CLIENT_TYPES = ('mobile', 'web')`. */
export const WEB_CLIENT_TYPE = 'web';

export interface SessionEmployee {
    id: number;
    code: string;
    name: string;
    privilege: number | null;
    /** Present on SESSION_ME only. */
    privilege_name?: string | null;
    /** Present on SESSION_ME only. */
    default_page?: string | null;
}

export interface SessionInfo {
    session_id: number;
    client_type: string;
    device_id: string;
    device_label: string | null;
    issued_at: string | null;
    idle_expires_at: string | null;
    absolute_expires_at: string | null;
}

export interface SessionCreateResult {
    token: string;
    session: SessionInfo;
    employee: SessionEmployee;
}

export interface SessionMeResult {
    session_id: number;
    employee: SessionEmployee;
    client_type: string;
    device_id: string;
    device_label: string | null;
    issued_at: string | null;
    last_seen_at: string | null;
    idle_expires_at: string | null;
    absolute_expires_at: string | null;
}

export interface SessionRevokeResult {
    revoked: number;
}

export interface SessionCreateInput {
    code: string;
    password: string;
    device_id: string;
    device_label?: string | null;
    /** Sent ONLY as a real boolean `true`, and only after the user confirmed. */
    replace_existing?: boolean;
}

const headersFor = (operation: string) => ({
    'Content-Type': 'application/json',
    'operation': operation,
});

const post = async (operation: string, body: Record<string, unknown>, skipAuth: boolean): Promise<unknown> => {
    try {
        const response = await axios.post(SESSION_ENDPOINT, JSON.stringify(body), {
            headers: headersFor(operation),
            skipAuth,
        });
        return response?.data;
    } catch (error) {
        throw toAuthApiError(error);
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return !!value && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Signs in. Resolves with the token and the employee, or rejects with an
 * `AuthApiError` the login screen branches on (401 / 409 / 429 / ...).
 *
 * Goes out WITHOUT a bearer: it must work with no session, and a 401 here
 * has to mean "bad credentials", not "your session died".
 */
export const session_create = async (input: SessionCreateInput): Promise<SessionCreateResult> => {
    const body: Record<string, unknown> = {
        code: input.code,
        password: input.password,
        client_type: WEB_CLIENT_TYPE,
        device_id: input.device_id,
    };
    if (input.device_label) {
        body.device_label = input.device_label;
    }
    // Omitted entirely unless it is exactly `true`: the Backend rejects
    // `'true'`, `1` and other loose values on purpose, and not sending it
    // keeps an ordinary login request incapable of revoking anything.
    if (input.replace_existing === true) {
        body.replace_existing = true;
    }

    const data = await post('SESSION_CREATE', body, true);
    if (!isRecord(data) || typeof data.token !== 'string' || data.token.length === 0 || !isRecord(data.employee)) {
        throw new AuthApiError(MALFORMED_RESPONSE_MESSAGE, { status: null });
    }
    return data as unknown as SessionCreateResult;
};

/** Validates the stored token and returns the authoritative actor. */
export const session_me = async (): Promise<SessionMeResult> => {
    const data = await post('SESSION_ME', {}, false);
    if (!isRecord(data) || !isRecord(data.employee)) {
        throw new AuthApiError(MALFORMED_RESPONSE_MESSAGE, { status: null });
    }
    return data as unknown as SessionMeResult;
};

/**
 * The signed-in employee's EFFECTIVE permission keys (Scan POS P1
 * `SESSION_PERMISSIONS`): only effective ALLOWs, for showing or hiding a
 * feature. Never stored -- the Backend still checks every action.
 */
export const session_permissions = async (): Promise<string[]> => {
    const data = await post('SESSION_PERMISSIONS', {}, false);
    if (!isRecord(data) || !Array.isArray(data.permissions)) {
        throw new AuthApiError(MALFORMED_RESPONSE_MESSAGE, { status: null });
    }
    return data.permissions.filter((p: unknown): p is string => typeof p === 'string');
};

/** Signs the current session out on the Backend. */
export const session_revoke = async (): Promise<SessionRevokeResult> => {
    const data = await post('SESSION_REVOKE', {}, false);
    const revoked = isRecord(data) && typeof data.revoked === 'number' ? data.revoked : 0;
    return { revoked };
};
